import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Card } from '#pie/components/card/Card';
import { DataTable } from '#pie/components/data-table/DataTable';
import { ErrorMessageBlock } from '#pie/components/error-message/ErrorMessageBlock';
import { Page, PageHeader } from '#pie/components/page/Page';
import { PageHead } from '#pie/components/page-head/PageHead';
import { Stack } from '#pie/components/stack/Stack';
import { SurveyDialogueStatusTag } from '#pie/components/survey-dialogue-status-tag/SurveyDialogueStatusTag';
import { SurveyDialogueToolbar } from '#pie/components/survey-dialogue-toolbar/SurveyDialogueToolbar';
import { Text } from '#pie/components/text/Text';
import { getFormattedDate } from '#pie/i18n/dateFormats';
import type { EffortScore, SurveySubject } from '#pie/internal/api/pie-bff';
import { useGetSurveyDialogue } from '#pie/internal/api/pie-bff';
import { cesFiltersAtom, cesSortingAtom } from '#pie/stores/ces';
import { paginationAtom } from '#pie/stores/pagination';
import { parseDates } from '#pie/utils/date';

export const SurveyDialogue = () => {
  const { t } = useTranslation();
  const { t: transtic } = useTranslation('ticket');

  const pagination = useAtomValue(paginationAtom);
  const sorting = useAtomValue(cesSortingAtom);
  const sortCol = sorting[0];
  const filter = useAtomValue(cesFiltersAtom);

  const { data, error, isLoading } = useGetSurveyDialogue(
    {
      filter,
      pagination,
      sorting: sortCol && { descending: sortCol.desc, field: sortCol.id },
    },
    {
      query: {
        select: (data) => ({
          ...data,
          items: data.items.map((item) => ({
            ...parseDates(item, ['createdOn']),
          })),
        }),
      },
    },
  );

  return (
    <>
      <PageHead title={t('survey_dialogue_overview.title')} />
      <Page>
        <PageHeader title={t('survey_dialogue_overview.title')} />
        <Stack as="section" gap="lg">
          <Text className="max-w-[960px] whitespace-pre-wrap">
            {t('survey_dialogue_overview.description')}
          </Text>
          <Card className="flex-1 overflow-x-hidden p-4">
            <Stack gap="md">
              <SurveyDialogueToolbar />
              {error ? (
                <ErrorMessageBlock error={error} />
              ) : (
                <DataTable
                  data={data?.items}
                  sortingAtom={cesSortingAtom}
                  isLoading={isLoading}
                  totalCount={data?.totalCount}
                  rowLinks={(row) => ({
                    'aria-label': row.original.ticketNumber,
                    to: `/ticketfeedback/${row.original.id}`,
                  })}
                  columns={(helper) => [
                    helper.accessor('ticketNumber', {
                      header: transtic('ticket_number'),
                    }),
                    helper.accessor('subject', {
                      cell: (info) =>
                        t(
                          `survey_dialogue.subject.${
                            info.getValue() as SurveySubject
                          }`,
                        ),
                      header: t('common.subject'),
                      meta: { columnWidth: 300 },
                    }),
                    helper.accessor('regionalGridOperatorHandler.label', {
                      header: t('common.contactperson'),
                      meta: { columnWidth: 300 },
                    }),
                    helper.accessor('status', {
                      cell: (info) => {
                        const status = info.getValue();
                        return (
                          <SurveyDialogueStatusTag
                            status={status}
                            className="float-left w-40"
                          />
                        );
                      },
                      header: t('common.status'),
                    }),
                    helper.accessor('createdOn', {
                      cell: (info) => {
                        const date = info.getValue();
                        return date && getFormattedDate(date);
                      },
                      header: t('common.submitted_on'),
                    }),
                    helper.accessor('effortScore', {
                      cell: (info) =>
                        t(
                          `survey_dialogue.effort_score.${
                            info.getValue() as EffortScore
                          }`,
                        ),
                      header: t(
                        'survey_dialogue_overview.table.header.effort_score',
                      ),
                    }),
                  ]}
                />
              )}
            </Stack>
          </Card>
        </Stack>
      </Page>
    </>
  );
};
